// Face ID convenience-unlock layer. Sits in front of the existing Auth0
// redirect — does NOT replace authentication. Phase 1 just gates the
// `/auth` redirect on a successful biometric prompt; silent re-auth via
// `prompt=none` will land in Phase 2 once the backend forwards the param.
//
// Privacy: only a non-sensitive boolean flag is stored locally to mark
// "this device has logged in before". No tokens, no emails.

import { NativeBiometric } from "capacitor-native-biometric";
import { isMobileApp } from "./isMobileApp";

const SESSION_FLAG_KEY = "asksmart.sessionExists";
const SILENT_TRIED_KEY = "asksmart.silentAuthInProgress";

export function setSessionFlag() {
  try {
    localStorage.setItem(SESSION_FLAG_KEY, "1");
  } catch (_) {}
}

export function clearSessionFlag() {
  try {
    localStorage.removeItem(SESSION_FLAG_KEY);
  } catch (_) {}
  try {
    sessionStorage.removeItem(SILENT_TRIED_KEY);
  } catch (_) {}
}

export function hasSessionFlag(): boolean {
  try {
    return localStorage.getItem(SESSION_FLAG_KEY) === "1";
  } catch (_) {
    return false;
  }
}

// Prevents a second Face ID prompt on the same launch if Auth0 bounces the
// user back to `/`. Cleared automatically when the app process ends because
// it lives in sessionStorage. Scaffold for Phase 2 — read but not enforced
// in Phase 1 since there's no silent-auth retry loop yet.
export function silentAlreadyTried(): boolean {
  try {
    return sessionStorage.getItem(SILENT_TRIED_KEY) === "1";
  } catch (_) {
    return false;
  }
}

export function markSilentTried() {
  try {
    sessionStorage.setItem(SILENT_TRIED_KEY, "1");
  } catch (_) {}
}

export type GateMode = "authenticated" | "interactive";
export interface GateResult {
  mode: GateMode;
  reason:
    | "not-mobile"
    | "no-session-flag"
    | "biometry-unavailable"
    | "user-cancelled"
    | "auth-failed"
    | "success"
    | "unexpected-error";
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

// Returns 'authenticated' only when the user successfully completed Face ID
// (or Touch ID) on a mobile device that has prior login state. Every other
// path returns 'interactive', which the caller treats as "go to Auth0 the
// normal way". Branch-level logs make staging/simulator debugging easy.
export async function runFaceIdGate(): Promise<GateResult> {
  if (!isMobileApp()) {
    console.log("[FaceID] gate result:", { mode: "interactive", reason: "not-mobile" });
    return { mode: "interactive", reason: "not-mobile" };
  }

  if (!hasSessionFlag()) {
    console.log("[FaceID] gate result:", { mode: "interactive", reason: "no-session-flag" });
    return { mode: "interactive", reason: "no-session-flag" };
  }

  let availability;
  try {
    availability = await NativeBiometric.isAvailable();
  } catch (err) {
    console.log("[FaceID] isAvailable threw — falling through:", err);
    return { mode: "interactive", reason: "biometry-unavailable" };
  }

  if (!availability?.isAvailable) {
    console.log("[FaceID] gate result:", {
      mode: "interactive",
      reason: "biometry-unavailable",
      biometryType: availability?.biometryType,
    });
    return { mode: "interactive", reason: "biometry-unavailable" };
  }

  // Let the loading screen paint before iOS overlays the prompt — without
  // this, cold-start prompts can sit on top of a black/unpainted webview.
  await sleep(250);

  try {
    await NativeBiometric.verifyIdentity({
      reason: "Unlock AskSmart",
      title: "AskSmart",
      subtitle: "Use Face ID to continue",
      description: "Authenticate to access your AskSmart account.",
    });
    console.log("[FaceID] gate result:", {
      mode: "authenticated",
      reason: "success",
      biometryType: availability.biometryType,
    });
    return { mode: "authenticated", reason: "success" };
  } catch (err: any) {
    // capacitor-native-biometric rejects on cancel and on auth failure with
    // an error object containing `code`. We treat both as fall-through; the
    // user just gets the normal Auth0 login screen.
    const code = err?.code ?? err?.errorCode;
    const isCancel = code === "10" || code === 10 || /cancel/i.test(String(err?.message ?? ""));
    const reason = isCancel ? "user-cancelled" : "auth-failed";
    console.log("[FaceID] gate result:", { mode: "interactive", reason, code, message: err?.message });
    return { mode: "interactive", reason };
  }
}
